import { useState } from 'react';
import { Form } from 'formik';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { Button } from '@gokrew/ui/components/Button';
import { AuthLayout } from '@gokrew/ui/layouts/AuthLayout';
import { KollabHRLogo } from '@gokrew/ui/assets';
import { Eye, EyeOff } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { resetForgotPasswordSchema } from '@gokrew/ui/validation/authSchema';
import { useMutation } from '@tanstack/react-query';
import { resetForgotPassword } from '@/api/auth';
import toast from 'react-hot-toast';
import { PasswordValidationIndicator } from '@gokrew/ui/components/PasswordValidationIndicator';
import type { ForgotPasswordResponse } from '@gokrew/models/auth';

interface ResetFormValues {
  newPassword: string;
  confirmPassword: string;
}

export function ResetPasswordPage() {
  const { t } = useTranslation(TranslationNS_Admin.Auth);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token');

  const { mutate, isPending } = useMutation<
    ForgotPasswordResponse,
    Error,
    { token: string; newPassword: string }
  >({
    mutationFn: ({ token, newPassword }) =>
      resetForgotPassword(token, newPassword),
    onSuccess: () => {
      toast.success(t('reset_password.toast.success'));
      navigate('/login');
    },
    onError: () => {
      toast.error(t('reset_password.toast.error'));
    },
  });

  const initialValues: ResetFormValues = {
    newPassword: '',
    confirmPassword: '',
  };

  return (
    <AuthLayout>
      <div className="bg-[#F9F5FF] rounded-2xl m-4 p-8 sm:p-12">
        <div className="text-center mb-6">
          <div className="flex justify-center w-full">
            <img className="w-20 mb-4" src={KollabHRLogo} alt="Logo" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('reset_password.title')}
          </h2>
          <p className="text-gray-600">{t('reset_password.subtitle')}</p>
        </div>

        <FormikWithSchema
          initialValues={initialValues}
          validationSchema={resetForgotPasswordSchema}
          onSubmit={(values) => {
            if (!token) {
              toast.error(t('reset_password.invalid_token'));
              navigate('/login');
              return;
            }
            mutate({ token, newPassword: values.newPassword });
          }}
        >
          {({ isValid, dirty, values }) => (
            <Form>
              <FormField
                name="newPassword"
                type={showPass ? 'text' : 'password'}
                label={t('reset_password.new_password')}
                placeholder={t('reset_password.new_password')}
                IconSuffix={showPass ? Eye : EyeOff}
                onIconClick={() => setShowPass(!showPass)}
              />

              <FormField
                name="confirmPassword"
                type={showConfirmPass ? 'text' : 'password'}
                label={t('reset_password.confirm_password')}
                placeholder={t('reset_password.confirm_password')}
                IconSuffix={showConfirmPass ? Eye : EyeOff}
                onIconClick={() => setShowConfirmPass(!showConfirmPass)}
              />

              <PasswordValidationIndicator
                password={values.newPassword}
                confirmPassword={values.confirmPassword}
                showMatchValidation={true}
                className="my-4"
              />

              <Button
                type="submit"
                disabled={!isValid || !dirty || isPending}
                className="w-full mt-1.5 mb-3"
              >
                {isPending ? '...' : t('reset_password.submit')}
              </Button>

              <p className="text-center text-gray-600 pt-2">
                {t('reset_password.return_to_login')}{' '}
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('reset_password.login')}
                </Link>
              </p>
            </Form>
          )}
        </FormikWithSchema>
      </div>
    </AuthLayout>
  );
}
